import { api } from "./api";
import {
  ListRequest,
  ListRequestSchemaV2,
  MessageResponse,
  PaginatedResponse,
} from "../types/common/commonTypes";
import {
  ProdRevCreate,
  WorkOrderCreate,
  WorkOrderUpdate,
  WorkOrderFull,
  PaginatedWOResponse,
  WorkOrderListView,
} from "../types/workOrderTypes";
import { OrderStatusUpdate } from "../types/combinedTypes";
/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const wo_base = "/manufacturing/orders";
export const workOrderService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: WorkOrderCreate): Promise<WorkOrderFull> => {
    const response = await api.post(`${wo_base}/`, data);
    return response.data;
  },
  listv2: async (params: ListRequestSchemaV2): Promise<PaginatedWOResponse> => {
    const response = await api.post(`${wo_base}/list_view`, params);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedWOResponse> => {
    const response = await api.post(`${wo_base}/search`, params);
    return response.data;
  },
  listPallets: async (): Promise<PaginatedResponse<WorkOrderListView>> => {
    const response = await api.get(`${wo_base}/pallet_orders`);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<WorkOrderFull> => {
    const response = await api.get(`${wo_base}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: WorkOrderUpdate): Promise<WorkOrderFull> => {
    const response = await api.put(`${wo_base}/${id}`, { ...data, id });
    return response.data;
  },
  markComplete: async (id: string): Promise<WorkOrderFull> => {
    const response = await api.put(`${wo_base}/${id}/complete`, { id });
    return response.data;
  },
  markVoid: async (id: string): Promise<WorkOrderFull> => {
    const response = await api.put(`${wo_base}/${id}/void`, { id });
    return response.data;
  },
  build: async (id: string, data: ProdRevCreate): Promise<WorkOrderFull> => {
    const response = await api.put(`${wo_base}/build/${id}`, { ...data, id });
    return response.data;
  },
  statusUpdate: async (data: OrderStatusUpdate): Promise<MessageResponse> => {
    const response = await api.post(`${wo_base}/status-update`, data);
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${wo_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<WorkOrderFull> => {
    const response = await api.post(`${wo_base}/${id}/restore`);
    return response.data;
  },
};
